import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { ShippingsService } from '@app/shared/services/entities/shippings.service';
import { MaterialRequestsService } from '@app/logistics/logistics.service';

/**
 * Recepción de delivery (DLV) — proceso "Entrada de material" del forms.
 *
 * La entrada registra CANTIDAD; la identidad (etiqueta RFID) se asigna
 * después. Al aceptar, el API crea el documento ENTRADA con folio REC-n,
 * una pieza SIN etiqueta por cada unidad recibida (Disponibles desde ya)
 * y su renglón de bitácora — el inventario cuadra desde el minuto uno.
 */
@Component({
  standalone: true,
  selector: 'app-reception-dialog',
  imports: [CommonModule, FormsModule, MatDialogModule, MaterialModule],
  templateUrl: './reception-dialog.component.html',
  styleUrls: ['./reception-dialog.component.scss'],
})
export class ReceptionDialogComponent implements OnInit {
  public warehouseName = '';
  public productsList: any[] = [];

  public deliveryFolio: string | null = null;
  public isOther = false;          // proceso "entrega de material — otro"
  public origin: string | null = null;
  public arrivedAsStated = true;
  public exclusiveDelivery = true;
  public carrierName: string | null = null;
  public comments: string | null = null;

  public rows: any[] = [];
  /** Insumos que venían en el delivery y NO llegaron (se guardan en `backorder`). */
  public backorder: { product: string; code: string; description: string; quantity: number }[] = [];
  public backorderFilter = '';
  public backorderQuantity = 1;
  /** Insumos que llegaron de más, que no venían en el DLV (se guardan en `extra`). */
  public extra: { product: string; code: string; description: string; quantity: number }[] = [];
  public extraFilter = '';
  /** Solicitud de material que surte esta entrada (la deja Recibida). */
  public materialRequest: string | null = null;
  public requestOptions: any[] = [];
  public productFilter = '';
  /** Cantidad con la que se agrega el insumo elegido en el buscador. */
  public addQuantity = 1;
  public ocrRunning = false;
  public ocrProgress = 0;

  constructor(
    public dialogRef: MatDialogRef<ReceptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private customMessageService: CustomMessageService,
    private shippingsService: ShippingsService,
    private requestsService: MaterialRequestsService,
  ) {}

  private async loadRequests(): Promise<void> {
    const place = this.data?.place;
    if (!place) return;
    try {
      const r: any = await this.requestsService.search({ searchType: 'default', condition: { deleted: false, place } });
      this.requestOptions = ((r?.data ?? []) as any[])
        .filter(x => ['Nueva', 'En surtido', 'Surtida'].includes(x.status))
        .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
    } catch { this.requestOptions = []; }
  }

  requestLabel(r: any): string {
    const codes = (r?.lines ?? []).map((l: any) => `${l.code} ×${l.quantity}`).join(', ');
    return `${r.folio} · ${r.status}${codes ? ' · ' + codes : ''}`;
  }

  public get filteredBackorder(): any[] {
    const f = this.backorderFilter.trim().toLowerCase();
    if (!f) return [];
    return this.productsList
      .filter(p => String(p.code ?? '').toLowerCase().includes(f) || String(p.description ?? '').toLowerCase().includes(f))
      .slice(0, 30);
  }

  addBackorder(product: any): void {
    const same = this.backorder.find(b => b.product === product.id);
    if (same) same.quantity += 1;
    else this.backorder.push({ product: product.id, code: product.code, description: product.description, quantity: 1 });
    this.backorderFilter = '';
  }

  removeBackorder(i: number): void { this.backorder.splice(i, 1); }

  public get filteredExtra(): any[] {
    const f = this.extraFilter.trim().toLowerCase();
    if (!f) return [];
    return this.productsList
      .filter(p => String(p.code ?? '').toLowerCase().includes(f) || String(p.description ?? '').toLowerCase().includes(f))
      .slice(0, 30);
  }

  addExtra(product: any): void {
    const same = this.extra.find(b => b.product === product.id);
    if (same) same.quantity += 1;
    else this.extra.push({ product: product.id, code: product.code, description: product.description, quantity: 1 });
    this.extraFilter = '';
  }

  removeExtra(i: number): void { this.extra.splice(i, 1); }

  public get extraPieces(): number {
    return this.extra.reduce((a, b) => a + (Number(b.quantity) || 0), 0);
  }

  public get backorderPieces(): number {
    return this.backorder.reduce((a, b) => a + (Number(b.quantity) || 0), 0);
  }

  /** Nombre y al menos un apellido. */
  public get operatorOk(): boolean {
    return String(this.carrierName ?? '').trim().split(/\s+/).filter(w => w.length >= 2).length >= 2;
  }

  ngOnInit(): void {
    this.warehouseName = this.data?.warehouseName ?? '';
    this.productsList = this.data?.productsList ?? [];
    // El módulo de Recepción abre el diálogo ya sabiendo el proceso:
    // Entrada DLV (false) o Entrada-otro (true).
    if (typeof this.data?.isOther === 'boolean') this.isOther = this.data.isOther;
    void this.loadRequests();
  }

  public get filteredProducts(): any[] {
    const f = this.productFilter.trim().toLowerCase();
    if (!f) return [];
    return this.productsList
      .filter(p => String(p.code ?? '').toLowerCase().includes(f) || String(p.description ?? '').toLowerCase().includes(f))
      .slice(0, 30);
  }

  addRow(product: any): void {
    this.rows.push({
      product: product.id,
      code: product.code,
      description: product.description,
      batch: null,
      expiration: null,
      quantity: 1,
    });
    this.productFilter = '';
  }

  // ─── Crear la tabla a partir de una foto del documento de entrega ──────────

  /**
   * Lee la foto con OCR (tesseract.js, en el navegador) y arma renglones:
   * busca en cada línea un CÓDIGO del catálogo de insumos, toma como
   * cantidad el último número entero de la línea y como lote el texto que
   * sigue al código. Es un BORRADOR: los renglones quedan editables para
   * corregir lo que la lectura no haya captado bien.
   */
  async fromPhoto(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    this.ocrRunning = true;
    this.ocrProgress = 0;
    try {
      const { createWorker } = await import('tesseract.js');
      const worker = await createWorker('spa+eng', 1, {
        logger: (m: any) => { if (m?.status === 'recognizing text') this.ocrProgress = Math.round((m.progress ?? 0) * 100); },
      });
      const { data } = await worker.recognize(file);
      await worker.terminate();
      const added = this.rowsFromText(String(data?.text ?? ''));
      await this.customMessageService.open({
        title: added ? 'Tabla leída' : 'Sin coincidencias', type: added ? 'success' : 'warning',
        message: added
          ? `Se agregaron <b>${added}</b> renglón(es) desde la foto. Revisa lote, caducidad y cantidad antes de aceptar.`
          : 'No se reconoció ningún código del catálogo en la foto. Intenta con una foto más nítida y de frente, o captura los renglones a mano.',
        dismissBtn: true,
      });
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: 'No se pudo leer la foto: ' + String(err), dismissBtn: true });
    } finally {
      this.ocrRunning = false;
    }
  }

  private rowsFromText(text: string): number {
    const norm = (v: any) => String(v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    // Códigos del catálogo (normalizados), del más largo al más corto para
    // que un código que contiene a otro no se confunda.
    const catalog = this.productsList
      .filter(p => p?.code)
      .map(p => ({ p, key: norm(p.code) }))
      .filter(x => x.key.length >= 4)
      .sort((a, b) => b.key.length - a.key.length);
    let added = 0;
    for (const rawLine of text.split(/\r?\n/)) {
      const line = rawLine.trim();
      if (!line) continue;
      const tokens = line.split(/\s+/);
      let hit: { p: any; key: string } | undefined;
      let hitIndex = -1;
      tokens.forEach((t, i) => {
        if (hit) return;
        const k = norm(t);
        const found = catalog.find(c => k === c.key || (k.length >= c.key.length && k.includes(c.key)));
        if (found) { hit = found; hitIndex = i; }
      });
      if (!hit) continue;
      const ints = tokens.slice(hitIndex + 1).map(t => t.replace(/[,.]/g, '')).filter(t => /^\d{1,4}$/.test(t)).map(Number);
      const quantity = ints.length ? ints[ints.length - 1] : 1;
      const batchToken = tokens.slice(hitIndex + 1).find(t => /[A-Z0-9]{4,}/i.test(t) && /\d/.test(t) && !/^\d{1,4}$/.test(t));
      const dateToken = line.match(/(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})|(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})/);
      let expiration: string | null = null;
      if (dateToken) {
        expiration = dateToken[1]
          ? `${dateToken[1]}-${dateToken[2].padStart(2, '0')}-${dateToken[3].padStart(2, '0')}`
          : `${dateToken[6]}-${dateToken[5].padStart(2, '0')}-${dateToken[4].padStart(2, '0')}`;
      }
      this.rows.push({
        product: hit.p.id,
        code: hit.p.code,
        description: hit.p.description,
        batch: batchToken ? batchToken.toUpperCase() : null,
        expiration,
        quantity: quantity > 0 ? quantity : 1,
        fromPhoto: true,
      });
      added++;
    }
    return added;
  }

  /** Piezas: entero, mínimo 1 (nunca 0 ni negativo). */
  fixQty(row: { quantity: number }): void {
    const n = Math.floor(Number(row.quantity));
    row.quantity = Number.isFinite(n) && n >= 1 ? Math.min(n, 9999) : 1;
  }

  step(row: { quantity: number }, delta: number): void {
    this.fixQty(row);
    row.quantity = Math.max(1, Math.min(9999, row.quantity + delta));
  }

  removeRow(index: number): void {
    this.rows.splice(index, 1);
  }

  public get totalPieces(): number {
    return this.rows.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  }

  async save(): Promise<void> {
    if (this.rows.length === 0) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Agrega al menos un renglón de material.', dismissBtn: true });
      return;
    }
    if (!this.isOther && !this.operatorOk) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Escribe el nombre completo del operador que entrega (nombre y apellido).', dismissBtn: true });
      return;
    }
    this.rows.forEach(r => this.fixQty(r));
    this.backorder.forEach(b => this.fixQty(b));
    this.extra.forEach(b => this.fixQty(b));
    // Llegó tal cual el DLV: no hay pendientes ni extras.
    const differs = !this.arrivedAsStated;
    const backorderPieces = differs ? this.backorderPieces : 0;
    const extraPieces = differs ? this.extraPieces : 0;
    if (this.rows.some(r => !r.quantity || r.quantity < 1)) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Todos los renglones necesitan cantidad mayor a cero.', dismissBtn: true });
      return;
    }
    const confirmed = await this.customMessageService.open({
      title: 'Confirmar recepción', type: 'neutral',
      message: `Se registra la entrada de <b>${this.totalPieces} piezas</b> para <b>${this.warehouseName}</b>${backorderPieces ? `, <b>${backorderPieces}</b> pendiente(s) en backorder` : ''}${extraPieces ? `, <b>${extraPieces}</b> extra(s)` : ''}${this.materialRequest ? '. La solicitud ligada queda <b>Recibida</b>' : ''}. Las piezas entran al locker cuando se asocian a su etiqueta RFID (acción "RFID" de la bitácora). ¿Aceptar la entrada?`,
      dismissBtn: true, acceptBtn: true, acceptBtnText: 'Aceptar entrada',
    });
    if (!confirmed) return;

    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Registrando recepción...', loading: true });
    try {
      const response: any = await this.shippingsService.receive({
        warehouse: this.data.warehouse,
        type: 'ENTRADA',
        deliveryFolio: this.deliveryFolio ?? undefined,
        arrivedAsStated: this.arrivedAsStated,
        exclusiveDelivery: this.exclusiveDelivery,
        carrierName: this.carrierName ?? undefined,
        origin: this.isOther ? (this.origin ?? undefined) : undefined,
        comments: this.comments ?? undefined,
        materialRequest: this.materialRequest ?? undefined,
        backorder: differs ? this.backorder.map(b => ({ product: b.product, quantity: Number(b.quantity) })) : [],
        extra: differs ? this.extra.map(b => ({ product: b.product, quantity: Number(b.quantity) })) : [],
        rows: this.rows.map(r => ({
          product: r.product,
          batch: r.batch || undefined,
          expiration: r.expiration || undefined,
          quantity: Number(r.quantity),
        })),
      });
      this.customMessageService.close();
      if (response?.state === 'success') {
        const doc = response.data?.[0]?.shipping;
        // Primero se cierra la captura (ya quedó guardada) y luego se avisa:
        // así el aviso nunca deja el diálogo abierto.
        this.dialogRef.close(true);
        void this.customMessageService.open({
          title: '¡Hecho!', type: 'success',
          message: `Recepción <b>${doc?.fileNumber ?? ''}</b> registrada: ${this.totalPieces} piezas por etiquetar (acción RFID de la bitácora).`,
          duration: 2500,
        });
      } else {
        await this.customMessageService.open({ title: 'Error', type: 'error', message: response?.message || 'No se pudo registrar la recepción.', dismissBtn: true });
      }
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true, dismissBtnText: 'Cerrar' });
    }
  }
}
